import React, { FC, useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Popover, CircularProgress } from '@mui/material';
import { useStacklineTheme, hexToRgba } from '@stackline/ui';
import AppProfileIcon from 'src/components/BeaconV2Components/Header/AppProfileIcon';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { NotificationBadge } from 'src/components/common/NotificationBadge';
import { useAppSelector, useHistory } from 'src/utils/Hooks';
import useNotificationsBadge from 'src/utils/Hooks/useNotificationsBadge';
import {
  getDivisions,
  getCurrentDivisionId,
  saveCurrentDivisionId,
  saveDivisions,
  CURRENT_DIVISION_KEY
} from 'src/utils/divisionStorage';
import { v2Validate, v2IssueToken, V2ValidateResponse, DivisionMembership } from 'src/store/modules/auth/v2Operations';
import { DIVISION_VALIDATED_EVENT } from 'src/hooks/useDivisionCheck';
import { isV2Enabled } from 'src/utils/authVersion';

interface AccountDivisionPopoverProps {
  appName: string;
}

const SEARCH_ICON = (
  <svg viewBox="0 0 24 24" fill="none" width="16" height="16" style={{ flexShrink: 0, opacity: 0.18 }}>
    <circle cx="11" cy="11" r="8" stroke="#7E8FA8" strokeWidth="2" />
    <path d="M21 21l-4.35-4.35" stroke="#7E8FA8" strokeWidth="2" />
  </svg>
);

const WARNING_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      stroke="#F35379"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const DISMISSED_MISMATCH_KEY = 'sl-division-mismatch-dismissed';

function isDismissedMismatch(divisionId: string): boolean {
  try { return sessionStorage.getItem(DISMISSED_MISMATCH_KEY) === divisionId; } catch { return false; }
}

function setDismissedMismatch(divisionId: string): void {
  try { sessionStorage.setItem(DISMISSED_MISMATCH_KEY, divisionId); } catch {}
}

function clearDismissedMismatch(): void {
  try { sessionStorage.removeItem(DISMISSED_MISMATCH_KEY); } catch {}
}

const AccountDivisionPopover: FC<AccountDivisionPopoverProps> = ({ appName }) => {
  const isV2 = isV2Enabled();
  const history = useHistory();
  const theme = useStacklineTheme();
  const avatarRef = useRef<HTMLDivElement>(null);

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [switching, setSwitching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [divisions, setDivisions] = useState<DivisionMembership[] | null>(() => getDivisions());
  const [activeDivisionId, setActiveDivisionId] = useState<string | null>(() => getCurrentDivisionId());

  // Cross-tab division change detection (2a)
  const [divisionMismatch, setDivisionMismatch] = useState(false);
  const [mismatchDivisionName, setMismatchDivisionName] = useState<string | null>(null);
  const [mismatchDivisionId, setMismatchDivisionId] = useState<string | null>(null);
  const [alertAnchor, setAlertAnchor] = useState<HTMLElement | null>(null);

  const { searchParams } = useAppSelector((state) => state.app.queryParams);
  const session = useAppSelector((state) => state.user?.session);
  const { email, isAdmin } = session || {};
  const reduxDivisionId = session?.divisionId;
  const currentDivisionId = activeDivisionId || reduxDivisionId;

  const shouldShowNotificationBadge = useNotificationsBadge(
    (state) =>
      state.notificationBadges.presentations || state.notificationBadges.export || state.notificationBadges.adjustments
  );

  const role = isAdmin ? 'Admin' : 'User';

  // Fetch fresh divisions on mount
  useEffect(() => {
    if (!isV2) return;
    const fetchDivisions = async () => {
      try {
        const response = await v2Validate();
        if (response.authenticated && response.divisions?.length > 0) {
          saveDivisions(response.divisions);
          setDivisions(response.divisions);
          if (response.division?.id) {
            setActiveDivisionId(response.division.id);
          }
        }
      } catch (error) {
        console.error('[AccountDivisionPopover] Failed to fetch divisions:', error);
      }
    };
    fetchDivisions();
  }, [isV2]);

  // 2a: Listen for cross-tab division changes via localStorage (same-origin)
  // and v2Validate on visibilitychange (cross-origin, e.g. Advisor switched)
  const mismatchAlreadyDetectedRef = useRef(false);

  useEffect(() => {
    if (!isV2) return;

    const triggerMismatch = (newId: string, newName: string) => {
      setDivisionMismatch(true);
      setMismatchDivisionName(newName);
      setMismatchDivisionId(newId);
      if (!mismatchAlreadyDetectedRef.current) {
        mismatchAlreadyDetectedRef.current = true;
        setAlertAnchor(avatarRef.current);
      }
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== CURRENT_DIVISION_KEY) return;
      const newDivisionId = e.newValue;
      if (newDivisionId && newDivisionId !== currentDivisionId && !isDismissedMismatch(newDivisionId)) {
        const cachedDivisions = getDivisions();
        const newDiv = cachedDivisions?.find((d) => d.id === newDivisionId);
        triggerMismatch(newDivisionId, newDiv?.name || newDivisionId);
      }
    };

    const handleDivisionValidated = (e: Event) => {
      if (!currentDivisionId) return;
      const response = (e as CustomEvent<V2ValidateResponse>).detail;
      if (!response.authenticated || !response.division) return;
      if (response.division.id !== currentDivisionId && !isDismissedMismatch(response.division.id)) {
        triggerMismatch(response.division.id, response.division.name || response.division.id);
        saveDivisions(response.divisions || []);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(DIVISION_VALIDATED_EVENT, handleDivisionValidated);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(DIVISION_VALIDATED_EVENT, handleDivisionValidated);
    };
  }, [isV2, currentDivisionId]);

  const filteredDivisions = useMemo(() => {
    if (!divisions) return [];
    if (!searchQuery.trim()) return divisions;
    const q = searchQuery.toLowerCase();
    return divisions.filter((d) => d.name?.toLowerCase().includes(q) || d.companyName?.toLowerCase().includes(q));
  }, [divisions, searchQuery]);

  const currentDivision = divisions?.find((d) => d.id === currentDivisionId);
  const showDivisionList = isV2 && divisions && divisions.length > 1;

  const handleAvatarClick = () => {
    if (divisionMismatch) {
      setAlertAnchor(avatarRef.current);
      return;
    }
    setPopoverAnchor(avatarRef.current);
    setSearchQuery('');
  };

  const handleClosePopover = () => {
    setPopoverAnchor(null);
    setSearchQuery('');
  };

  const handleCloseAlert = () => {
    setAlertAnchor(null);
  };

  const handleDivisionSwitch = useCallback(
    async (divisionId: string) => {
      try {
        setSwitching(true);
        handleClosePopover();

        const validateResponse = await v2Validate();
        if (!validateResponse.authenticated || !validateResponse.sessionToken) {
          window.location.href = '/sign_out';
          return;
        }
        if (validateResponse.divisions?.length > 0) {
          saveDivisions(validateResponse.divisions);
        }

        await v2IssueToken(validateResponse.sessionToken, divisionId, appName);
        saveCurrentDivisionId(divisionId);
        clearDismissedMismatch();
        window.location.reload();
      } catch (error) {
        console.error('[AccountDivisionPopover] Failed to switch division:', error);
        setSwitching(false);
      }
    },
    [currentDivisionId, appName]
  );

  const handleStay = async () => {
    setAlertAnchor(null);
    if (currentDivisionId) {
      await handleDivisionSwitch(currentDivisionId);
    }
  };

  const handleReloadWithNewDivision = () => {
    setAlertAnchor(null);
    clearDismissedMismatch();
    window.location.reload();
  };

  const handleSettings = () => {
    handleClosePopover();
    if (!window.location.pathname.includes('account')) {
      history.push(`/account/profile${searchParams}`);
    }
  };

  const handleSignOut = () => {
    handleClosePopover();
    history.push('/sign_out');
  };

  const popoverOpen = Boolean(popoverAnchor);
  const alertOpen = Boolean(alertAnchor);
  const selectedBg = hexToRgba(theme.colors.primaryLight, 0.3);
  const hoverBg = hexToRgba(theme.colors.secondaryPorcelain, 1);
  const borderColor = theme.colors.primaryGray;

  if (switching) {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <CircularProgress size={16} />
      </div>
    );
  }

  return (
    <>
      {/* Avatar trigger */}
      <div
        ref={avatarRef}
        style={{ height: 30, width: 30, cursor: 'pointer', position: 'relative' }}
        role="button"
        onClick={handleAvatarClick}
      >
        <div
          style={{
            borderRadius: '50%',
            boxShadow: divisionMismatch ? '0 0 0 3px #F35379' : 'none',
            width: 30,
            height: 30,
            position: 'relative'
          }}
        >
          <AppProfileIcon shouldShowNotificationBadge={shouldShowNotificationBadge} size={30} />
          {divisionMismatch && (
            <div
              style={{
                position: 'absolute',
                top: -2,
                right: -2,
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: '#F35379',
                border: '2px solid #F6F8FA'
              }}
            />
          )}
        </div>
      </div>

      {/* 2a: Division changed alert popover */}
      <Popover
        open={alertOpen}
        anchorEl={alertAnchor}
        onClose={handleCloseAlert}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        PaperProps={{
          sx: {
            width: 320,
            borderRadius: '8px',
            border: '1px solid #dedede',
            boxShadow: '0 0 16px 0 rgba(0,0,0,0.06)',
            padding: '24px',
            marginTop: '12px',
            overflow: 'visible'
          }
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: '#FFEEED',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 12
          }}
        >
          {WARNING_ICON}
        </div>
        <div style={{ fontSize: 18, fontWeight: 600, lineHeight: '24px', marginBottom: 8 }}>Division Changed</div>
        <div style={{ fontSize: 14, lineHeight: '20px', letterSpacing: '0.08px', marginBottom: 20 }}>
          You switched to <strong>{mismatchDivisionName}</strong> in another app. This page was loaded with{' '}
          <strong>{currentDivision?.name || currentDivisionId}</strong>.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleStay}
            style={{
              flex: 1,
              height: 32,
              background: '#fff',
              color: '#031425',
              border: '1px solid #E8E8ED',
              borderRadius: 4,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'Roboto, sans-serif'
            }}
          >
            Stay
          </button>
          <button
            onClick={handleReloadWithNewDivision}
            style={{
              flex: 1,
              height: 32,
              background: '#031425',
              color: '#fff',
              border: '1px solid #031425',
              borderRadius: 4,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'Roboto, sans-serif'
            }}
          >
            Reload Page
          </button>
        </div>
      </Popover>

      {/* 1a: Account + Division popover */}
      <Popover
        open={popoverOpen}
        anchorEl={popoverAnchor}
        onClose={handleClosePopover}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        PaperProps={{
          sx: {
            width: 280,
            borderRadius: '8px',
            border: `1px solid ${borderColor}`,
            boxShadow: '0px 4px 14px 0px rgba(0,0,0,0.1)',
            marginTop: '8px',
            overflow: 'hidden',
            padding: 0,
            '& ul': { padding: 0 }
          }
        }}
      >
        {/* Email + Role header */}
        <div
          style={{
            padding: '14px 12px',
            borderBottom: `1px solid ${borderColor}`
          }}
        >
          <Text variant="subtitle2" fontWeight="bold">
            {email}
          </Text>
          <div style={{ fontSize: 12, lineHeight: '18px', color: '#7E8FA8', marginTop: 4, opacity: 0.5 }}>{role}</div>
        </div>

        {/* Division search + list */}
        {showDivisionList && (
          <>
            <div
              style={{
                height: 40,
                padding: '0 12px',
                borderBottom: `1px solid ${borderColor}`,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              {SEARCH_ICON}
              <input
                type="text"
                placeholder="Search divisions"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  width: '100%',
                  height: '100%',
                  fontSize: 14,
                  color: '#031425',
                  fontFamily: 'Roboto, sans-serif'
                }}
              />
            </div>
            <div style={{ maxHeight: 252, overflowY: 'auto', background: '#fff' }}>
              {filteredDivisions.map((division) => {
                const isSelected = division.id === currentDivisionId;
                return (
                  <div
                    key={division.id}
                    onClick={() => { if (division.id !== currentDivisionId) handleDivisionSwitch(division.id); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: 36,
                      padding: '8px 20px',
                      cursor: 'pointer',
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: '20px',
                      color: '#031425',
                      borderBottom: `1px solid ${borderColor}`,
                      background: isSelected ? selectedBg : '#fff',
                      transition: 'background 0.1s'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) (e.currentTarget as HTMLElement).style.background = '#EDF5FF';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background = isSelected ? selectedBg : '#fff';
                    }}
                  >
                    {division.companyName ? `${division.companyName} - ${division.name}` : (division.name || division.id)}
                  </div>
                );
              })}
              {filteredDivisions.length === 0 && (
                <div style={{ padding: '12px 20px', fontSize: 13, color: '#7E8FA8', textAlign: 'center' }}>
                  No divisions found
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer actions */}
        <div style={{ borderTop: showDivisionList ? 'none' : undefined }}>
          <button
            onClick={handleSettings}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '14px 12px',
              fontSize: 14,
              fontWeight: 500,
              lineHeight: '20px',
              borderTop: `1px solid ${borderColor}`,
              border: 'none',
              borderTopStyle: 'solid',
              borderTopWidth: 1,
              borderTopColor: borderColor,
              background: 'transparent',
              cursor: 'pointer',
              fontFamily: 'Roboto, sans-serif',
              color: '#031425',
              transition: 'background 0.1s'
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = '#F6F8FA';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'transparent';
            }}
          >
            Settings
            {shouldShowNotificationBadge && <NotificationBadge variant="small" />}
          </button>
          <button
            onClick={handleSignOut}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '14px 12px',
              fontSize: 14,
              fontWeight: 500,
              lineHeight: '20px',
              borderTop: `1px solid ${borderColor}`,
              border: 'none',
              borderTopStyle: 'solid',
              borderTopWidth: 1,
              borderTopColor: borderColor,
              background: 'transparent',
              cursor: 'pointer',
              fontFamily: 'Roboto, sans-serif',
              color: '#031425',
              transition: 'background 0.1s'
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = '#F6F8FA';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'transparent';
            }}
          >
            Sign out
          </button>
        </div>
      </Popover>
    </>
  );
};

export default AccountDivisionPopover;
